import { useCallback } from "react";

import { useComponentStateHooks } from "../state/ComponentStateContext";

// When a defaultValue is provided, the returned value is guaranteed non-undefined.
export function useProperty<T>(
  id: string,
  propertyName: string,
  options: { defaultValue: T }
): [T, (value: T) => void, () => void];

// Without a defaultValue the returned value may be undefined.
export function useProperty<T>(
  id: string,
  propertyName: string,
  options?: { defaultValue?: T }
): [T | undefined, (value: T) => void, () => void];

export function useProperty<T>(
  id: string,
  propertyName: string,
  options?: {
    defaultValue?: T;
  }
): [T | undefined, (value: T) => void, () => void] {
  const { useValue, useSetValue, useRemoveValue } = useComponentStateHooks();

  const defaultValue = options?.defaultValue;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- unsound-by-design generic accessor: the store holds unknown and T is the caller's claim about their own property
  const propertyValue = useValue(id, propertyName, defaultValue) as
    T | undefined;

  const setPropertyValueFn = useSetValue();
  const removePropertyValueFn = useRemoveValue();

  const setValue = useCallback(
    (value: T) => {
      setPropertyValueFn(id, propertyName, value);
    },
    [id, propertyName, setPropertyValueFn]
  );

  const removeValue = useCallback(() => {
    removePropertyValueFn(id, propertyName);
  }, [id, propertyName, removePropertyValueFn]);

  return [propertyValue, setValue, removeValue];
}
